import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  InputSignalWithTransform,
  OutputEmitterRef,
  booleanAttribute,
  input,
  output
} from '@angular/core';

import { MaskPipe } from 'tiime-expert-utils';
import {
  FallbackTextDirective,
  TiimeButtonModule,
  TiimeIconModule,
  TiimeLargePropertyDirective,
  TiimePropertyDirective,
  TiimeTooltipModule
} from 'tiime-material';

import { SIRET_MASK } from '@constants/mask.constant';
import { Company } from '@models/company';
import { TaxRegime } from '@models/tax-regime';
import { VatSystem } from '@models/vat-system';
import { BooleanInputType } from '@modules/core/types/boolean-input.type';
import { AddressPropertyComponent } from '@shared-components/address-property/address-property.component';
import { RefreshButtonComponent } from '@shared-components/refresh-button/refresh-button.component';
import { CodeAndLabelPipe } from '@shared-pipes/code-and-label/code-and-label.pipe';
import { IncludesPipe } from '@shared-pipes/includes/includes.pipe';
import { TaxRegimeLabelPipe } from '@shared-pipes/tax-regime-label/tax-regime-label.pipe';

export type DisplayedCompanyProperty =
  | 'name'
  | 'reference'
  | 'legalForm'
  | 'apeCode'
  | 'taxRegime'
  | 'vatSystem'
  | 'shareCapital'
  | 'activityStartDate'
  | 'missionEndDate';

@Component({
  selector: 'app-company-properties',
  standalone: true,
  imports: [
    CommonModule,
    TiimeTooltipModule,
    TiimeLargePropertyDirective,
    TiimePropertyDirective,
    TiimeButtonModule,
    TiimeIconModule,
    TaxRegimeLabelPipe,
    FallbackTextDirective,
    IncludesPipe,
    RefreshButtonComponent,
    CodeAndLabelPipe,
    AddressPropertyComponent,
    MaskPipe
  ],
  templateUrl: './company-properties.component.html',
  styleUrls: ['./company-properties.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompanyPropertiesComponent {
  @Input() company: Company;
  @Input() taxRegimes: TaxRegime[];
  @Input() vatSystems: VatSystem[];
  @Input({ required: true }) displayedProperties: DisplayedCompanyProperty[];
  readonly showRefresh: InputSignalWithTransform<boolean, BooleanInputType> = input<boolean, BooleanInputType>(false, {
    transform: booleanAttribute
  });
  readonly showNavigateToCompany: InputSignalWithTransform<boolean, BooleanInputType> = input<
    boolean,
    BooleanInputType
  >(false, {
    transform: booleanAttribute
  });

  readonly refreshCompany: OutputEmitterRef<void> = output<void>();
  readonly navigateToCompany: OutputEmitterRef<void> = output<void>();

  readonly siretMask = SIRET_MASK;

  emitRefreshCompany(): void {
    this.refreshCompany.emit();
  }

  emitNavigateToCompany(): void {
    this.navigateToCompany.emit();
  }
}
